import React, { useState } from 'react'
import { api } from '../../utils/api'
import { useAuth } from '../../hooks/useAuth'
import { ConsignmentBadge } from '../shared/ConsignmentBadge.jsx'
import AppLink from '../shared/AppLink.jsx'

// Bin/shelf stock lives in the Prism (Python) warehouse backend. Stock Check
// does ONE call, reached via the /api/inventory-locations proxy:
//   - GET /items?shelf_id=X → every item on that exact shelf, already joined
//     to display data (title, images[], quantity, shopify_status,
//     shopify_product_id, current_shelf_id, current_position, ownership,
//     split_pct). The backend's shelf_id filter does the
//     item_location ⨝ inventory_item ⨝ snapshot join server-side.
//
// This replaced an N+1 fan-out (one /item-locations lookup + one /items lookup
// per item). That storm hammered the rate-limited backend (10 r/s per egress
// IP) into 503s, and every 503 landed in a per-item catch that silently dropped
// the item — the "random results" bug (different subset returned each search).
//
// "Product name" mode makes the same call with q=<text> instead of shelf_id.
// The backend matches every word in any order and includes items with no bin.

const MOVE_REASON = 'reorganisation'
const RESULT_LIMIT = 200
const PUTAWAY_PATH = '/warehouse/putaway'

const SEARCH_BY_KEY = 'stockCheck.searchBy'
const SEARCH_BY = {
  bin: {
    label: 'Bin',
    placeholder: 'e.g. LIT-1-B-3-5-6',
    help: 'Enter a bin/shelf id to see every item currently in stock there.',
    param: 'shelf_id',
  },
  name: {
    label: 'Product name',
    placeholder: 'e.g. Bugera 333XL',
    help: 'Finds every word in any order, with or without a bin.',
    param: 'q',
  },
}
const DEFAULT_SEARCH_BY = 'bin'

function readSearchBy() {
  try {
    const saved = localStorage.getItem(SEARCH_BY_KEY)
    return saved in SEARCH_BY ? saved : DEFAULT_SEARCH_BY
  } catch {
    // Storage can be blocked (private window). The default is a safe answer.
    return DEFAULT_SEARCH_BY
  }
}

function saveSearchBy(mode) {
  try {
    localStorage.setItem(SEARCH_BY_KEY, mode)
  } catch {
    // Storage can be blocked. The choice just is not remembered next visit.
  }
}

function rowThumb(images) {
  if (!Array.isArray(images) || images.length === 0) return null
  const src = images[0]
  if (typeof src !== 'string') return src?.src || src?.url || null
  if (src.includes('cdn.shopify.com')) {
    return `${src}${src.includes('?') ? '&' : '?'}width=120`
  }
  return src
}

function statusLabel(item) {
  if (item.shopify_status === 'active' && (item.quantity ?? 0) > 0) return { text: 'In stock', cls: 'badge-success' }
  if (item.shopify_status === 'active') return { text: 'Out of stock', cls: 'badge-warning' }
  if (item.shopify_status === 'draft') return { text: 'Draft', cls: 'badge-info' }
  if (item.shopify_status === 'archived') return { text: 'Archived', cls: 'badge-neutral' }
  return { text: item.shopify_status || '—', cls: 'badge-neutral' }
}

function StockCheck() {
  const { hasPermission } = useAuth()
  const canEdit = hasPermission('warehouse')

  const [searchBy, setSearchBy] = useState(readSearchBy)
  const [binQuery, setBinQuery] = useState('')
  const [activeBin, setActiveBin] = useState('')
  const [activeMode, setActiveMode] = useState(DEFAULT_SEARCH_BY)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [error, setError] = useState(null)

  const [movingId, setMovingId] = useState(null)
  const [moveValue, setMoveValue] = useState('')
  const [busyId, setBusyId] = useState(null)

  function chooseSearchBy(mode) {
    setSearchBy(mode)
    saveSearchBy(mode)
  }

  async function load(binValue, mode = searchBy) {
    const bin = (binValue ?? binQuery).trim()
    if (!bin) return
    setLoading(true)
    setError(null)
    setMovingId(null)
    try {
      // One deterministic query: every item on this exact shelf, joined to
      // display data server-side (see the note at the top of the file).
      const res = await api.get(
        `/api/inventory-locations/items?${SEARCH_BY[mode].param}=${encodeURIComponent(bin)}&limit=${RESULT_LIMIT}`
      )
      const list = Array.isArray(res) ? res : (Array.isArray(res?.items) ? res.items : [])
      const rows = list.map(it => ({
        item_id: it.item_id,
        shelf_id: it.current_shelf_id || (mode === 'bin' ? bin : null),
        position: it.current_position ?? null,
        title: it.title || it.item_id,
        images: it.images || [],
        quantity: it.quantity,
        shopify_status: it.shopify_status,
        shopify_product_id: it.shopify_product_id,
        ownership: it.ownership,
        split_pct: it.split_pct
      }))

      // Stock check shows only what's currently in stock (quantity >= 1).
      const inStock = rows.filter(r => (Number(r.quantity) || 0) >= 1)
      inStock.sort((a, b) => (a.position || '').localeCompare(b.position || ''))
      setItems(inStock)
      setActiveBin(bin)
      setActiveMode(mode)
      setSearched(true)
    } catch (err) {
      console.error('Stock check search failed:', err)
      setError(err.message || 'Search failed')
      setItems([])
      setSearched(true)
    } finally {
      setLoading(false)
    }
  }

  function handleSearch(e) {
    e.preventDefault()
    load()
  }

  function startMove(item) {
    setMovingId(item.item_id)
    setMoveValue('')
  }

  function cancelMove() {
    setMovingId(null)
    setMoveValue('')
  }

  async function saveMove(item) {
    const toShelf = moveValue.trim()
    if (!toShelf || toShelf === item.shelf_id) {
      cancelMove()
      return
    }
    if (!item.position) {
      alert('This item has no recorded position on the shelf, so it cannot be moved from here.')
      return
    }
    setBusyId(item.item_id)
    try {
      await api.post('/api/inventory-locations/move', {
        item_id: item.item_id,
        from_shelf: item.shelf_id,
        from_position: String(item.position),
        to_shelf: toShelf,
        reason: MOVE_REASON
        // to_position omitted → backend allocates the next free slot.
      })
      cancelMove()
      await load(activeBin, activeMode)
    } catch (err) {
      console.error('Move failed:', err)
      alert('Failed to move item: ' + (err.message || 'Unknown error'))
    } finally {
      setBusyId(null)
    }
  }

  // "Remove from stock" — set the Shopify inventory level to 0 rather than
  // hard-deleting. The item and its location stay; it just reads out of stock.
  async function removeItem(item) {
    if (!item.shopify_product_id) {
      alert('This item has no linked Shopify product, so its stock cannot be set here.')
      return
    }
    const confirmed = window.confirm(
      `Set "${item.title}" to 0 stock?\n\nIt will read as out of stock. The item and its location are kept and it can be restocked later.`
    )
    if (!confirmed) return
    setBusyId(item.item_id)
    try {
      await api.put(`/api/inventory/shopify/product/${item.shopify_product_id}`, { quantity: 0 })
      await load(activeBin, activeMode)
    } catch (err) {
      console.error('Remove failed:', err)
      alert('Failed to update stock: ' + (err.message || 'Unknown error'))
    } finally {
      setBusyId(null)
    }
  }

  const byName = searchBy === 'name'
  const resultsByName = activeMode === 'name'
  const totalQty = items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0)

  return (
    <div>
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <form onSubmit={handleSearch}>
          <div role="group" aria-label="Search by" style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
            <span className="filter-label">Search by</span>
            <span style={{ display: 'inline-flex', gap: 'var(--space-1)' }}>
              {Object.entries(SEARCH_BY).map(([mode, option]) => (
                <button
                  key={mode}
                  type="button"
                  className={`btn btn-sm ${searchBy === mode ? 'btn-primary' : 'btn-secondary'}`}
                  aria-pressed={searchBy === mode}
                  onClick={() => chooseSearchBy(mode)}
                >
                  {option.label}
                </button>
              ))}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', marginTop: 'var(--space-2)' }}>
            <input
              type="text"
              className="form-input"
              style={{ maxWidth: 360 }}
              aria-label={SEARCH_BY[searchBy].label}
              placeholder={SEARCH_BY[searchBy].placeholder}
              value={binQuery}
              onChange={e => setBinQuery(e.target.value)}
              autoFocus
            />
            <button type="submit" className="btn btn-primary" disabled={loading || !binQuery.trim()}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
          <p className="text-sm text-muted" style={{ marginTop: 'var(--space-2)' }}>
            {SEARCH_BY[searchBy].help}
          </p>
        </form>
      </div>

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light, #fee2e2)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {!error && (
      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : !searched ? (
          <div className="empty-state">
            <div className="empty-state-title">Stock check by {byName ? 'product name' : 'bin'}</div>
            <div className="empty-state-description">
              {byName ? 'Search a product name above to find it, with or without a bin.' : 'Search a bin/shelf id above to list everything located there.'}
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">
              {resultsByName ? `Nothing in stock matching “${activeBin}”` : `Nothing in stock in “${activeBin}”`}
            </div>
            <div className="empty-state-description">
              {resultsByName ? 'No in-stock items have all of these words in their title.' : 'No in-stock items are located in this bin.'}
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 var(--space-1) var(--space-3)' }}>
              <span className="text-sm" style={{ fontWeight: 500 }}>
                {items.length} item{items.length === 1 ? '' : 's'} {resultsByName ? 'matching' : 'in'} “{activeBin}”
              </span>
              <span className="text-sm text-muted">Total qty: {totalQty}</span>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Image</th>
                  <th>Title</th>
                  <th style={{ textAlign: 'center', width: 56 }}>Qty</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 130 }}>Item</th>
                  <th style={{ width: 160 }}>{resultsByName ? 'Bin' : 'Location'}</th>
                  {canEdit && <th style={{ width: 200 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const img = rowThumb(item.images)
                  const isMoving = movingId === item.item_id
                  const isBusy = busyId === item.item_id
                  const st = statusLabel(item)
                  return (
                    <tr key={item.item_id} className={isBusy ? 'loading' : ''}>
                      <td>
                        {img ? (
                          <img src={img} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }} />
                        ) : (
                          <div style={{ width: 48, height: 48, background: 'var(--color-gray-100, #f3f4f6)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-gray-400, #9ca3af)', fontSize: 12 }}>
                            No img
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.title}
                          {item.ownership === 'consignment' && (
                            <span style={{ marginLeft: 6 }}><ConsignmentBadge splitPct={item.split_pct} /></span>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>{item.quantity ?? '—'}</td>
                      <td><span className={`badge ${st.cls}`}>{st.text}</span></td>
                      <td className="text-sm text-muted" style={{ fontFamily: 'monospace', fontSize: 12 }}>{item.item_id || '—'}</td>
                      <td>
                        {isMoving ? (
                          <input
                            type="text"
                            className="form-input"
                            style={{ padding: '4px 8px', fontSize: '0.85rem' }}
                            value={moveValue}
                            onChange={e => setMoveValue(e.target.value)}
                            placeholder="New bin/shelf id"
                            autoFocus
                            onKeyDown={e => {
                              if (e.key === 'Enter') { e.preventDefault(); saveMove(item) }
                              if (e.key === 'Escape') cancelMove()
                            }}
                          />
                        ) : !item.shelf_id && resultsByName ? (
                          <span className="badge badge-warning">No bin</span>
                        ) : (
                          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {item.shelf_id || '—'}{item.position ? ` · ${item.position}` : ''}
                          </span>
                        )}
                      </td>
                      {canEdit && (
                        <td>
                          {isMoving ? (
                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                              <button className="btn btn-sm btn-primary" onClick={() => saveMove(item)} disabled={isBusy}>
                                {isBusy ? 'Saving...' : 'Save'}
                              </button>
                              <button className="btn btn-sm btn-secondary" onClick={cancelMove} disabled={isBusy}>
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                              {item.shelf_id ? (
                                <button className="btn btn-sm btn-secondary" onClick={() => startMove(item)} disabled={isBusy}>
                                  Move
                                </button>
                              ) : (
                                // Move needs a current position, so an unplaced item goes through Putaway.
                                <AppLink href={PUTAWAY_PATH} className="btn btn-sm btn-secondary">Put away</AppLink>
                              )}
                              <button className="btn btn-sm btn-danger" onClick={() => removeItem(item)} disabled={isBusy} title="Set stock to 0">
                                Remove
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
      )}
    </div>
  )
}

export default StockCheck
